/**
 * View 5, Critical roles: the roles HR flags critical in Role Master, how well
 * each is covered by successors, and who holds them (totals only). No risk level
 * for now (HR, 9 Oct 2026): HR judges the risk in its own analysis.
 */
import React, { useMemo } from 'react'
import type { ReportingPerson } from '@/services/api/talentReportingService'
import { MIDYEAR_RATING_LABEL } from '@/components/performance/manager/talent-review/eduvosBlocks'
import { BarList, Panel, Tile, TileRow } from './TalentDashboardParts'
import { RoleTable } from './RoleTable'
import { COVERAGE_COLUMNS } from './SuccessionView'
import { MOBILITY_ORDER, count, countBy, fmtPct, mobilityOf } from './talentDashboardData'
import { COVERAGE_BAND_LABEL, coverageBand, type CoverageBand, type RoleCoverage } from './talentRoleData'

const BANDS: CoverageBand[] = ['full', 'partial', 'none']
const RATING_ORDER = ['Exceptional', 'Full', 'Not yet full', 'Weak', 'Too soon to rate']

export const CriticalRolesView: React.FC<{
  rows: RoleCoverage[]
  holders: ReportingPerson[]
  positionsNote: string
  onExport?: (rows: RoleCoverage[], what: string) => void
  /** Gender and race: administrators and HR administrators only. */
  showDemographics?: boolean
}> = ({ rows, holders, positionsNote, onExport, showDemographics = true }) => {
  const bands = useMemo(
    () =>
      BANDS.map((b) => {
        const count = rows.filter((c) => coverageBand(c) === b).length
        return { label: COVERAGE_BAND_LABEL[b], count, share: rows.length ? Math.round((count / rows.length) * 100) : 0 }
      }),
    [rows],
  )

  if (!rows.length) {
    return (
      <Panel title="Critical roles">
        <p className="max-w-[70ch] text-sm text-slate-600">
          No roles with these filters are flagged critical in Role Master. HR is updating the list after the meeting
          on 13 October; this view fills in as roles are flagged.
        </p>
      </Panel>
    )
  }

  const withAny = rows.filter((c) => c.any > 0).length
  const withNow = rows.filter((c) => c.readyNow > 0).length
  const none = rows.length - withAny
  const rating = (p: ReportingPerson) => (p.midyear_rating ? MIDYEAR_RATING_LABEL[p.midyear_rating] || p.midyear_rating : null)

  return (
    <div className="space-y-4">
      <TileRow min="170px">
        <Tile label="Critical roles" value={rows.length} sub={`${rows.reduce((n, c) => n + c.role.positions, 0)} positions`} />
        <Tile label="With a successor" value={fmtPct(withAny, rows.length)} sub={`${count(withAny, 'role')}, any readiness`} />
        <Tile label="With a Ready Now successor" value={fmtPct(withNow, rows.length)} sub={count(withNow, 'role')} />
        <Tile label="No successor named" value={none} tone={none ? 'warn' : 'default'} sub={`${fmtPct(none, rows.length)} of critical roles`} />
      </TileRow>
      <p className="text-[11px] text-gray-500">{positionsNote}</p>

      <RoleTable
        title="Coverage of each critical role"
        note="Successors at each readiness, with their share of the role's positions."
        rows={[...rows].sort((a, b) => a.any / Math.max(a.role.positions, 1) - b.any / Math.max(b.role.positions, 1))}
        columns={COVERAGE_COLUMNS}
        onExport={onExport && (() => onExport(rows, 'Critical roles'))}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Coverage at any readiness"><BarList rows={bands} /></Panel>
        <Panel title="By mid-year rating of those holding them"><BarList rows={countBy(holders, rating, RATING_ORDER)} /></Panel>
      </div>

      <Panel
        title="Who holds critical roles"
        note={`${count(holders.length, 'person', 'people')} in scope. Totals only.${showDemographics ? '' : ' Gender and race are shown to administrators and HR administrators.'}`}
      >
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {showDemographics && (
            <>
              <div><h3 className="mb-2 text-xs font-semibold text-gray-600">Gender</h3><BarList rows={countBy(holders, (p) => p.gender)} /></div>
              <div><h3 className="mb-2 text-xs font-semibold text-gray-600">Race</h3><BarList rows={countBy(holders, (p) => p.race)} /></div>
            </>
          )}
          <div><h3 className="mb-2 text-xs font-semibold text-gray-600">Nationality</h3><BarList rows={countBy(holders, (p) => p.nationality)} max={8} /></div>
          <div><h3 className="mb-2 text-xs font-semibold text-gray-600">Employment type</h3><BarList rows={countBy(holders, (p) => p.employment_category)} /></div>
          <div><h3 className="mb-2 text-xs font-semibold text-gray-600">Mobility</h3><BarList rows={countBy(holders, mobilityOf, MOBILITY_ORDER)} /></div>
        </div>
      </Panel>
    </div>
  )
}
